<script setup lang="ts">
import MarkdownIt from 'markdown-it';
import type Renderer from 'markdown-it/lib/renderer.mjs';
import type Token from 'markdown-it/lib/token.mjs';
// @ts-expect-error - no types available
import markdownItTaskLists from 'markdown-it-task-lists';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { VISION_AI_SUMMARY_INTERACTIONS } from '@/components/VisionAi/constants';
import { parseMessageSegments } from '@/components/VisionAi/utils/message';
import type { VisionWidget } from '@/components/VisionAi/widgets/visionWidget.types';
import WidgetWrapper from '@/components/VisionAi/widgets/WidgetWrapper.vue';
import { RESOURCE_CENTER_BASE_URL } from '@/config';

type GroupedSegment =
  | { type: 'markdown'; content: string }
  | { type: 'widget-group'; ids: string[] };

interface Props {
  text?: string;
  widgets?: VisionWidget[];
  isExpanded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  widgets: () => [],
  isExpanded: false,
});

const emit = defineEmits<{
  messageInteraction: [interaction: string];
}>();

const router = useRouter();
const route = useRoute();

const markdown = new MarkdownIt({
  linkify: true,
  typographer: true,
  html: true,
}).use(markdownItTaskLists, { enabled: true });

markdown.renderer.rules.link_open = (
  tokens: Token[],
  idx: number,
  options: Parameters<Renderer['renderToken']>[2],
  _env: unknown,
  self: Renderer,
): string => {
  tokens[idx].attrPush(['target', '_blank']);
  tokens[idx].attrPush(['rel', 'noopener']);
  return self.renderToken(tokens, idx, options);
};

const widgetMap = computed(() => {
  const map = new Map<string, VisionWidget>();
  props.widgets.forEach((widget) => {
    if (widget.id) map.set(widget.id, widget);
  });
  return map;
});

const segments = computed(() => parseMessageSegments(props.text, widgetMap.value));

/**
 * Group consecutive widget segments so they can render side-by-side.
 * Whitespace-only markdown between widgets is absorbed into the group.
 */
const groupedSegments = computed((): GroupedSegment[] => {
  const result: GroupedSegment[] = [];
  let currentGroup: { type: 'widget-group'; ids: string[] } | null = null;

  for (const segment of segments.value) {
    if (segment.type === 'widget') {
      if (!currentGroup) {
        currentGroup = { type: 'widget-group', ids: [] };
      }
      currentGroup.ids.push(segment.id);
    } else if (segment.type === 'markdown') {
      if (currentGroup && segment.content.trim() === '') {
        // Whitespace between consecutive artifacts — skip it
        continue;
      }
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push({ type: 'markdown', content: segment.content });
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }

  return result;
});

function renderMarkdown(content: string): string {
  return markdown.render(content);
}

function getLinkInteractionType(href: string): string | null {
  try {
    const { pathname, origin } = new URL(href, window.location.origin);

    if (origin !== window.location.origin) {
      if (href.startsWith(RESOURCE_CENTER_BASE_URL)) {
        return VISION_AI_SUMMARY_INTERACTIONS.RESOURCE_CENTRE_CLICK;
      }
      return VISION_AI_SUMMARY_INTERACTIONS.WEB_CLICK;
    }

    // Try resolving as-is first (handles paths that already include the brand label).
    // If no named route resolves (e.g. a legacy catch-all matched instead), retry with
    // the current brand label prepended so the named routes under /:brandLabel are reached.
    let resolved = router.resolve(pathname);
    if (!resolved.name && route.params.brandLabel) {
      resolved = router.resolve(`/${route.params.brandLabel}${pathname}`);
    }

    const routeName = resolved.name ? String(resolved.name) : null;
    if (!routeName) return null;
    if (routeName.endsWith('.tweets') || routeName === 'library.media') {
      return VISION_AI_SUMMARY_INTERACTIONS.POST_CLICK;
    }
    if (routeName.startsWith('community.messages')) {
      return VISION_AI_SUMMARY_INTERACTIONS.COMMENT_CLICK;
    }
  } catch {
    // ignore invalid URLs
  }
  return null;
}

function handleMarkdownClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const link = target.closest('a');
  if (!link) return;
  const interaction = getLinkInteractionType(link.href);
  if (interaction) emit('messageInteraction', interaction);
}
</script>

<template>
  <div class="response-markdown w-full!" :class="{ 'popup-expanded': props.isExpanded }">
    <template v-for="(segment, idx) in groupedSegments" :key="idx">
      <div
        v-if="segment.type === 'markdown'"
        v-sanitize-html="renderMarkdown(segment.content)"
        class="markdown-content"
        @click="handleMarkdownClick"
      />
      <div
        v-else-if="segment.type === 'widget-group'"
        class="mb-(--space-16) flex w-full! flex-wrap gap-(--space-16)"
        data-testid="widget-group"
      >
        <template v-for="id in segment.ids" :key="id">
          <WidgetWrapper
            v-if="widgetMap.has(id)"
            :widgets="[widgetMap.get(id)!]"
            @on-widget-click="emit('messageInteraction', $event)"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<style lang="postcss" scoped>
/* Scoped to .markdown-content only - excludes widget-group (Dashing-UI components) */
.response-markdown {
  max-width: 100%;
}

.markdown-content {
  max-width: 100%;
  margin-bottom: var(--space-16);

  :deep() {
    b,
    strong {
      font-weight: 600;
      font-size: inherit;
    }

    p:not(:last-child) {
      margin-bottom: var(--space-16);
    }

    i,
    em {
      font-style: italic;
      font-size: inherit;
    }

    ul {
      list-style-type: disc;
      padding-left: var(--space-20);
    }

    ol {
      list-style-type: decimal;
      padding-left: var(--space-20);

      & > li > ol {
        list-style-type: lower-alpha;

        & > li > ol {
          list-style-type: lower-roman;
        }
      }
    }

    .task-list-item {
      list-style-type: none;
    }

    .task-list-item input {
      padding-left: var(--space-20);
    }

    table {
      display: block;
      max-height: 20.625rem;
      overflow: scroll;
      width: fit-content;
      max-width: 100%;
      background-color: var(--white);
      border-collapse: separate;
      border-radius: var(--round-corner);
      box-shadow: var(--shadow-1);
      margin-bottom: var(--space-16);
      padding: var(--space-16);

      thead tr th {
        color: var(--text-secondary);
        font-weight: var(--font-medium);
        padding: var(--space-8) var(--space-8);
        text-align: left;
      }

      thead tr th,
      tbody tr td {
        border-bottom: 1px solid var(--border);
        font-size: var(--x14);
      }

      tbody tr td {
        padding: var(--space-16) var(--space-8);
        vertical-align: top;
      }

      tbody tr:last-child > td {
        border-bottom: none;
      }

      img {
        max-width: 60px;

        .popup-expanded & {
          max-width: 189px;
        }
      }
    }

    blockquote {
      border-left: var(--space-4) solid var(--border);
      padding-left: var(--space-8);
      color: var(--text-secondary);
      font-style: italic;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-bottom: var(--space-24);
    }

    blockquote,
    h1:has(+ p, + ul, + ol),
    h2:has(+ p, + ul, + ol),
    h3:has(+ p, + ul, + ol),
    h4:has(+ p, + ul, + ol),
    h5:has(+ p, + ul, + ol),
    h6:has(+ p, + ul, + ol),
    ul:has(+ p, + ul, + ol),
    ol:has(+ p, + ul, + ol),
    p:last-child {
      margin-bottom: var(--space-16);
    }

    p:has(+ h1, + h2, + h3, + h4, + h5, + h6),
    ul:has(+ h1, + h2, + h3, + h4, + h5, + h6),
    ol:has(+ h1, + h2, + h3, + h4, + h5, + h6) {
      margin-bottom: var(--space-24);
    }

    h1 {
      font-size: var(--x32);
    }

    h2 {
      font-size: var(--x28);
    }

    h3 {
      font-size: var(--x24);
    }

    h4 {
      font-size: var(--x22);
    }

    h5 {
      font-size: var(--x18);
      font-weight: var(--font-bold);
    }

    h6 {
      font-size: var(--x16);
      font-weight: var(--font-bold);
    }

    a {
      font-weight: var(--font-normal);
      color: var(--text-link);
    }

    hr {
      width: 33%;
      margin: 0 auto var(--space-24) auto;
    }

    img {
      border-radius: 10px;
      display: block;
      max-width: 300px;
      max-height: 300px;

      .popup-expanded & {
        border-radius: 20px;
      }
    }

    ul,
    ol {
      margin-bottom: var(--space-16);
    }

    li {
      margin-bottom: var(--space-8);

      p {
        margin-bottom: var(--space-8) !important;
      }
    }
  }
}
</style>
